import {
  AndroidIcon,
  AppleIcon,
  AppleMusicIcon,
  BandcampIcon,
  BehanceIcon,
  BlueskyIcon,
  BufferIcon,
  CanvaIcon,
  ClubhouseIcon,
  DiscordIcon,
  DribbbleIcon,
  DropboxIcon,
  FacebookIcon,
  GbpIcon,
  GiphyIcon,
  GithubIcon,
  GoogleDriveIcon,
  GooglePhotosIcon,
  IconGoogleIcon,
  InstagramIcon,
  LinkedinIcon,
  MastodonIcon,
  MediumIcon,
  MessengerIcon,
  OneDriveIcon,
  PatreonIcon,
  PaypalIcon,
  PinterestIcon,
  ShopifyIcon,
  SnapchatIcon,
  SoundcloudIcon,
  SpotifyIcon,
  StartPageIcon,
  SubstackIcon,
  TelegramIcon,
  ThreadsIcon,
  TiktokIcon,
  TwitchIcon,
  TwitterIcon,
  UnsplashIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '../icons'
import type { IconComponent } from '../icons/helpers'

/**
 * Channel and integration icons the picker offers alongside its curated glyph
 * set. Unlike the lazily-loaded glyphs (raw geometry tinted via `currentColor`),
 * these reuse Popcorn's existing icon components directly rather than duplicating
 * their SVGs — the components already fill `currentColor`, so they inherit the
 * selected tint like any glyph.
 *
 * Keys are stable kebab-case identifiers, part of the public `IconName` contract
 * and persisted in saved-view identities. Insertion order is the grid order
 * (mirrors the design's "Channel and integration" frame), appended after the
 * glyph set. Keyword arrays are search aliases matched (by substring) alongside
 * the icon name; they must not repeat the name's own words.
 */
const COMPONENT_ICON_META = {
  buffer: ['social', 'scheduling', 'management', 'posting', 'marketing'],
  linkedin: ['li', 'professional', 'work', 'career', 'jobs'],
  patreon: ['members', 'subscriptions', 'creator', 'funding'],
  tiktok: ['tt', 'video', 'short', 'viral'],
  mastodon: ['fediverse', 'social', 'microblog', 'decentralized'],
  youtube: ['yt', 'video', 'streaming', 'creator'],
  facebook: ['fb', 'social', 'meta', 'network'],
  pinterest: ['pins', 'boards', 'inspiration', 'visual'],
  discord: ['chat', 'communities', 'gaming', 'voice'],
  instagram: ['ig', 'photos', 'visual', 'stories', 'meta'],
  'google-business': ['gbp', 'gmb', 'profile', 'listing', 'local'],
  x: ['twitter', 'tweet', 'social', 'network'],
  threads: ['meta', 'social', 'microblog', 'feed'],
  bluesky: ['social', 'microblog', 'network', 'atproto'],
  telegram: ['messaging', 'chat', 'social', 'channels'],
  'google-photos': ['storage', 'cloud', 'backup', 'media'],
  unsplash: ['photos', 'stock', 'free', 'images'],
  'start-page': ['bio', 'links', 'profile', 'landing'],
  bandcamp: ['music', 'artist', 'audio', 'streaming', 'indie'],
  messenger: ['messaging', 'chat', 'meta', 'direct'],
  behance: ['portfolio', 'creative', 'design', 'adobe'],
  google: ['search', 'web', 'integration'],
  medium: ['blog', 'writing', 'articles', 'publishing'],
  shopify: ['ecommerce', 'store', 'selling', 'commerce'],
  whatsapp: ['messaging', 'chat', 'meta', 'mobile'],
  substack: ['newsletter', 'writing', 'email', 'publishing'],
  clubhouse: ['audio', 'social', 'voice', 'rooms'],
  giphy: ['gifs', 'animation', 'visual'],
  onedrive: ['microsoft', 'storage', 'cloud', 'backup'],
  dropbox: ['storage', 'cloud', 'backup', 'sync'],
  'apple-music': ['streaming', 'audio', 'songs', 'podcasts'],
  snapchat: ['snap', 'social', 'messaging', 'stories'],
  dribbble: ['design', 'portfolio', 'creative', 'ui'],
  spotify: ['music', 'streaming', 'audio', 'podcasts'],
  soundcloud: ['audio', 'music', 'streaming', 'tracks'],
  canva: ['design', 'graphics', 'templates', 'creative'],
  twitch: ['streaming', 'live', 'gaming', 'video'],
  github: ['gh', 'code', 'repo', 'git'],
  paypal: ['payments', 'billing', 'money', 'checkout'],
  'google-drive': ['storage', 'cloud', 'files', 'backup'],
  android: ['mobile', 'os', 'development'],
  apple: ['ios', 'mac', 'ecosystem', 'mobile'],
} satisfies Record<string, readonly string[]>

/** Stable identifier for a channel/integration picker icon. */
export type ComponentIconName = keyof typeof COMPONENT_ICON_META

/** Popcorn icon component rendered for each component-icon name. */
const COMPONENT_ICONS: Record<ComponentIconName, IconComponent> = {
  buffer: BufferIcon,
  linkedin: LinkedinIcon,
  patreon: PatreonIcon,
  tiktok: TiktokIcon,
  mastodon: MastodonIcon,
  youtube: YoutubeIcon,
  facebook: FacebookIcon,
  pinterest: PinterestIcon,
  discord: DiscordIcon,
  instagram: InstagramIcon,
  'google-business': GbpIcon,
  x: TwitterIcon,
  threads: ThreadsIcon,
  bluesky: BlueskyIcon,
  telegram: TelegramIcon,
  'google-photos': GooglePhotosIcon,
  unsplash: UnsplashIcon,
  'start-page': StartPageIcon,
  bandcamp: BandcampIcon,
  messenger: MessengerIcon,
  behance: BehanceIcon,
  google: IconGoogleIcon,
  medium: MediumIcon,
  shopify: ShopifyIcon,
  whatsapp: WhatsappIcon,
  substack: SubstackIcon,
  clubhouse: ClubhouseIcon,
  giphy: GiphyIcon,
  onedrive: OneDriveIcon,
  dropbox: DropboxIcon,
  'apple-music': AppleMusicIcon,
  snapchat: SnapchatIcon,
  dribbble: DribbbleIcon,
  spotify: SpotifyIcon,
  soundcloud: SoundcloudIcon,
  canva: CanvaIcon,
  twitch: TwitchIcon,
  github: GithubIcon,
  paypal: PaypalIcon,
  'google-drive': GoogleDriveIcon,
  android: AndroidIcon,
  apple: AppleIcon,
}

const COMPONENT_ICON_SET: ReadonlySet<string> = new Set(
  Object.keys(COMPONENT_ICON_META),
)

/**
 * Narrows an arbitrary string to a {@link ComponentIconName}. Handy at
 * boundaries such as a persisted value that may name an icon no longer offered.
 */
export function isComponentIconName(value: string): value is ComponentIconName {
  return COMPONENT_ICON_SET.has(value)
}

export { COMPONENT_ICON_META, COMPONENT_ICONS }
